"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, ArrowRight, LayoutTemplate, Database, Server, Wrench, ExternalLink, Workflow, Compass, PenTool, Cpu, Cloud, FlaskConical, GitBranch, Brain, Bot } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";
import AudioPlayer from "@/components/AudioPlayer";
import DecryptText from "@/components/DecryptText";
import { useNeumorphicTheme } from "@/hooks/useNeumorphicTheme";
import Footer from "@/components/Footer";
import CarouselSection from "@/components/ui/CarouselSection";
import { AnimatedTimeline } from "@/components/animata/progress/animatedtimeline";
import { MorphingText } from "@/components/ui/morphing-text";
import RippleButton from "@/components/lightswind/ripple-button";
import { getAboutImageIndex } from "@/utils/aboutImage";
function EducationIcon(t0) {
  const $ = _c(8);
  const {
    size: t1,
    className: t2
  } = t0;
  const size = t1 === undefined ? 40 : t1;
  const className = t2 === undefined ? "" : t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <rect width="256" height="256" fill="none" stroke="none" />;
    t4 = <line x1="32" y1="64" x2="32" y2="144" />;
    t5 = <path d="M56,216c15.7-24.08,41.11-40,72-40s56.3,15.92,72,40" />;
    t6 = <polygon points="224 64 128 96 32 64 128 32 224 64" />;
    t7 = <path d="M169.34,82.22a56,56,0,1,1-82.68,0" />;
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
    $[3] = t6;
    $[4] = t7;
  } else {
    t3 = $[0];
    t4 = $[1];
    t5 = $[2];
    t6 = $[3];
    t7 = $[4];
  }
  let t8;
  if ($[5] !== className || $[6] !== size) {
    t8 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width={size} height={size} className={className} fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16">{t3}{t4}{t5}{t6}{t7}</svg>;
    $[5] = className;
    $[6] = size;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  return t8;
}
function ClearancesIcon(t0) {
  const $ = _c(9);
  const {
    size: t1,
    className: t2
  } = t0;
  const size = t1 === undefined ? 40 : t1;
  const className = t2 === undefined ? "" : t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <rect width="256" height="256" fill="none" stroke="none" />;
    t4 = <line x1="72" y1="136" x2="120" y2="136" />;
    t5 = <line x1="72" y1="104" x2="120" y2="104" />;
    t6 = <circle cx="196" cy="124" r="44" />;
    t7 = <path d="M168,192H40a8,8,0,0,1-8-8V56a8,8,0,0,1,8-8H216a8,8,0,0,1,8,8V90.06" />;
    t8 = <polyline points="168 157.94 168 224 196 208 224 224 224 157.94" />;
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
    $[3] = t6;
    $[4] = t7;
    $[5] = t8;
  } else {
    t3 = $[0];
    t4 = $[1];
    t5 = $[2];
    t6 = $[3];
    t7 = $[4];
    t8 = $[5];
  }
  let t9;
  if ($[6] !== className || $[7] !== size) {
    t9 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width={size} height={size} className={className} fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16">{t3}{t4}{t5}{t6}{t7}{t8}</svg>;
    $[6] = className;
    $[7] = size;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  return t9;
}
function CategoryIconMorph(t0) {
  const $ = _c(14);
  const {
    icon: DefaultIcon,
    hoverIcon: HoverIcon,
    isHovered,
    isNeumorphic
  } = t0;
  const t1 = `relative w-9 h-9 flex items-center justify-center p-1.5 border-2 transition-all duration-300 ${isNeumorphic ? "border-transparent text-black rounded-lg shadow-[inset_2px_2px_5px_rgba(163,177,198,0.5),_inset_-2px_-2px_5px_rgba(255,255,255,0.7)]" : "border-white text-white bg-white/10"}`;
  const t2 = `transition-all duration-300 transform ${isHovered ? "opacity-0 scale-50 rotate-90 pointer-events-none absolute" : "opacity-100 scale-100 rotate-0"}`;
  let t3;
  if ($[0] !== DefaultIcon) {
    t3 = <DefaultIcon size={22} />;
    $[0] = DefaultIcon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `transition-all duration-300 transform ${isHovered ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-50 -rotate-90 pointer-events-none absolute"}`;
  let t6;
  if ($[5] !== HoverIcon) {
    t6 = <HoverIcon size={22} />;
    $[5] = HoverIcon;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t1 || $[11] !== t4 || $[12] !== t7) {
    t8 = <div className={t1}>{t4}{t7}</div>;
    $[10] = t1;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
interface SkillItemMeta {
  name: string;
  role: string;
  color: string;
  iconUrl: string;
  url: string;
}
function SkillKeycap(t0) {
  const $ = _c(30);
  const {
    item,
    isNeumorphic
  } = t0;
  const [isHovered, setIsHovered] = useState(false);
  const [imgError, setImgError] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsHovered(true);
    t2 = () => setIsHovered(false);
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const t3 = `group/keycap relative w-full h-9.5 rounded-none cursor-pointer transition-all duration-200 select-none overflow-hidden block ${isNeumorphic ? isHovered ? "bg-[#d1d9e6] text-[#1e293b]" : "bg-[#e0e5ec] text-black" : isHovered ? "border-2 border-white bg-white text-black" : "border-2 border-white/80 bg-zinc-950 text-white"}`;
  const t4 = `${item.name} // ${item.role}`;
  const t5 = `${item.name} documentation (opens in a new tab)`;
  let t6;
  if ($[2] !== item.color) {
    t6 = <span className="w-2 h-2 rounded-full shrink-0 shadow-sm" style={{
      backgroundColor: item.color
    }} />;
    $[2] = item.color;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = `font-mono font-black uppercase transition-all duration-200 transform whitespace-nowrap ${item.name.length > 15 ? "text-[10.5px] sm:text-[11.5px] tracking-tighter" : item.name.length > 12 ? "text-[11px] sm:text-[12px] tracking-tight" : "text-xs sm:text-[12.5px] tracking-tight"} ${isHovered ? "opacity-0 -translate-y-4 pointer-events-none" : "opacity-100 translate-y-0"}`;
  let t8;
  if ($[4] !== item.name || $[5] !== t7) {
    t8 = <span className={t7}>{item.name}</span>;
    $[4] = item.name;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = `absolute font-mono font-bold text-[9px] sm:text-[9.5px] uppercase tracking-tight transition-all duration-200 transform whitespace-nowrap px-0.5 ${isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`;
  let t10;
  if ($[7] !== item.role || $[8] !== t9) {
    t10 = <span className={t9}>{item.role}</span>;
    $[7] = item.role;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t8) {
    t11 = <div className="flex-1 min-w-0 mx-1.5 relative h-full flex items-center justify-center overflow-hidden">{t8}{t10}</div>;
    $[10] = t10;
    $[11] = t8;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== imgError || $[14] !== isHovered || $[15] !== isNeumorphic || $[16] !== item.color || $[17] !== item.iconUrl || $[18] !== item.name) {
    t12 = !imgError ? <Image src={item.iconUrl} alt={item.name} width={16} height={16} onError={() => setImgError(true)} className="w-4 h-4 shrink-0 object-contain" style={{
      filter: isHovered && !isNeumorphic ? "invert(1) brightness(0)" : "none"
    }} /> : <span className="w-2 h-2 rounded-full shrink-0" style={{
      backgroundColor: item.color
    }} />;
    $[13] = imgError;
    $[14] = isHovered;
    $[15] = isNeumorphic;
    $[16] = item.color;
    $[17] = item.iconUrl;
    $[18] = item.name;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t11 || $[21] !== t12 || $[22] !== t6) {
    t13 = <div className="w-full h-full flex items-center justify-between px-2 sm:px-2.5 relative">{t6}{t11}{t12}</div>;
    $[20] = t11;
    $[21] = t12;
    $[22] = t6;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== item.url || $[25] !== t13 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5) {
    t14 = <a href={item.url} target="_blank" rel="noopener noreferrer" onMouseEnter={t1} onMouseLeave={t2} className={t3} title={t4} aria-label={t5}>{t13}</a>;
    $[24] = item.url;
    $[25] = t13;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}
export default function AboutClient(t0) {
  const $ = _c(285);
  const {
    dict,
    footerDict,
    locale
  } = t0;
  const [toastMessage, setToastMessage] = useState(null);
  const [isSummaryCardHovered, setIsSummaryCardHovered] = useState(false);
  const [isSkillsCardHovered, setIsSkillsCardHovered] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const isNeumorphic = useNeumorphicTheme();
  const [imageIndex, setImageIndex] = useState(_temp);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleCycle = () => {
        setImageIndex(getAboutImageIndex());
      };
      window.addEventListener("cycle-about-image", handleCycle);
      return () => {
        window.removeEventListener("cycle-about-image", handleCycle);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const {
    scrollY
  } = useScroll();
  const clipStartScroll = useMotionValue(0);
  const clipEndScroll = useMotionValue(315);
  const parallaxEndScroll = useMotionValue(600);
  let t3;
  let t4;
  if ($[2] !== clipEndScroll || $[3] !== clipStartScroll || $[4] !== parallaxEndScroll) {
    t3 = () => {
      const handleResize = () => {
        if (window.innerWidth < 768) {
          clipStartScroll.set(700);
          clipEndScroll.set(1200);
          parallaxEndScroll.set(1200);
        } else {
          clipStartScroll.set(0);
          clipEndScroll.set(315);
          parallaxEndScroll.set(600);
        }
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t4 = [clipStartScroll, clipEndScroll, parallaxEndScroll];
    $[2] = clipEndScroll;
    $[3] = clipStartScroll;
    $[4] = parallaxEndScroll;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const getProgress = _temp2;
  let t5;
  if ($[7] !== clipEndScroll || $[8] !== clipStartScroll) {
    t5 = y_0 => getProgress(y_0, clipStartScroll.get(), clipEndScroll.get()) * 115;
    $[7] = clipEndScroll;
    $[8] = clipStartScroll;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const imageBottomInset = useTransform(scrollY, t5);
  const clipPathImage = useTransform(imageBottomInset, _temp3);
  let t6;
  if ($[10] !== clipEndScroll || $[11] !== clipStartScroll) {
    t6 = y_1 => {
      const progress = getProgress(y_1, clipStartScroll.get(), clipEndScroll.get());
      return 100 - progress * 115;
    };
    $[10] = clipEndScroll;
    $[11] = clipStartScroll;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const filterTopInset = useTransform(scrollY, t6);
  const clipPathFilter = useTransform(filterTopInset, _temp4);
  let t7;
  if ($[13] !== parallaxEndScroll) {
    t7 = y_2 => {
      const progress_0 = getProgress(y_2, 0, parallaxEndScroll.get());
      return progress_0 * -40;
    };
    $[13] = parallaxEndScroll;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const imageY = useTransform(scrollY, t7);
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = message => {
      setToastMessage(message);
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const showToast = t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  useEffect(_temp5, t9);
  let t10;
  let t11;
  if ($[17] !== toastMessage) {
    t10 = () => {
      if (toastMessage) {
        const timer = setTimeout(() => {
          setToastMessage(null);
        }, 4000);
        return () => clearTimeout(timer);
      }
    };
    t11 = [toastMessage];
    $[17] = toastMessage;
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  useEffect(t10, t11);
  let section1;
  let section2;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  if ($[20] !== clipPathFilter || $[21] !== clipPathImage || $[22] !== dict || $[23] !== hoveredCardIndex || $[24] !== imageIndex || $[25] !== imageY || $[26] !== isNeumorphic || $[27] !== isSkillsCardHovered || $[28] !== isSummaryCardHovered || $[29] !== locale) {
    const stack = [{
      category: "Frontend & Design",
      icon: LayoutTemplate,
      hoverIcon: PenTool,
      items: [{
        name: "Next.js",
        role: "SSR & App Router",
        color: "#ffffff",
        iconUrl: "/skills/nextjs.svg",
        url: "https://nextjs.org/docs"
      }, {
        name: "React",
        role: "UI Components",
        color: "#61dafb",
        iconUrl: "/skills/react.svg",
        url: "https://react.dev"
      }, {
        name: "Angular",
        role: "Enterprise SPA",
        color: "#dd0031",
        iconUrl: "/skills/angular.svg",
        url: "https://angular.dev"
      }, {
        name: "TypeScript",
        role: "Typed JavaScript",
        color: "#3178c6",
        iconUrl: "/skills/typescript.svg",
        url: "https://www.typescriptlang.org/docs/"
      }, {
        name: "Tailwind CSS",
        role: "Utility Design Tokens",
        color: "#38bdf8",
        iconUrl: "/skills/tailwindcss.svg",
        url: "https://tailwindcss.com/docs"
      }]
    }, {
      category: "Backend & Database",
      icon: Database,
      hoverIcon: Cpu,
      items: [{
        name: "Java",
        role: "Enterprise OOP",
        color: "#f8981d",
        iconUrl: "/skills/java.svg",
        url: "https://docs.oracle.com/en/java/"
      }, {
        name: "Spring",
        role: "Backend Framework",
        color: "#6db33f",
        iconUrl: "/skills/spring.svg",
        url: "https://spring.io/docs"
      }, {
        name: "NestJS",
        role: "Modular Backend API",
        color: "#e0234e",
        iconUrl: "/skills/nestjs.svg",
        url: "https://docs.nestjs.com/"
      }, {
        name: "Express.js",
        role: "REST Middleware",
        color: "#a0a0a0",
        iconUrl: "/skills/express.svg",
        url: "https://expressjs.com/"
      }, {
        name: "Python",
        role: "Core Data & Scripting",
        color: "#3776ab",
        iconUrl: "/skills/python.svg",
        url: "https://docs.python.org/3/"
      }, {
        name: "Flask",
        role: "Micro Services",
        color: "#ffffff",
        iconUrl: "/skills/flask.svg",
        url: "https://flask.palletsprojects.com/"
      }, {
        name: "Postgres",
        role: "ACID Relational SQL",
        color: "#336791",
        iconUrl: "/skills/postgresql.svg",
        url: "https://www.postgresql.org/docs/"
      }, {
        name: "MongoDB",
        role: "NoSQL Document DB",
        color: "#47a248",
        iconUrl: "/skills/mongodb.svg",
        url: "https://www.mongodb.com/docs/"
      }]
    }, {
      category: "Architecture & DevOps",
      icon: Server,
      hoverIcon: Cloud,
      items: [{
        name: "Docker",
        role: "Containerization",
        color: "#2496ed",
        iconUrl: "/skills/docker.svg",
        url: "https://docs.docker.com/"
      }, {
        name: "Kubernetes",
        role: "Cluster Orchestration",
        color: "#326ce5",
        iconUrl: "/skills/kubernetes.svg",
        url: "https://kubernetes.io/docs/"
      }, {
        name: "Jenkins",
        role: "CI/CD Automation",
        color: "#d24939",
        iconUrl: "/skills/jenkins.svg",
        url: "https://www.jenkins.io/doc/"
      }, {
        name: "Linux OS",
        role: "Server Kernel",
        color: "#fcc624",
        iconUrl: "/skills/linux.svg",
        url: "https://docs.kernel.org/"
      }, {
        name: "Nexus Repo",
        role: "Artifact Registry",
        color: "#1c77c3",
        iconUrl: "/skills/nexus.svg",
        url: "https://help.sonatype.com/en/nexus-repository.html"
      }]
    }, {
      category: "API & Testing",
      icon: Wrench,
      hoverIcon: FlaskConical,
      items: [{
        name: "Postman",
        role: "API Testing Suite",
        color: "#ff6c37",
        iconUrl: "/skills/postman.svg",
        url: "https://learning.postman.com/docs/"
      }, {
        name: "Jest",
        role: "Unit Testing",
        color: "#9A425B",
        iconUrl: "/skills/jest.svg",
        url: "https://jestjs.io/docs/getting-started"
      }, {
        name: "Selenium",
        role: "E2E Automation",
        color: "#43b02a",
        iconUrl: "/skills/selenium.svg",
        url: "https://www.selenium.dev/documentation/"
      }, {
        name: "BeautifulSoup",
        role: "Web Scraping Engine",
        color: "#ffd43b",
        iconUrl: "/skills/beautifulsoup.svg",
        url: "https://www.crummy.com/software/BeautifulSoup/bs4/doc/"
      }, {
        name: "REST APIs",
        role: "HTTP API Standard",
        color: "#009688",
        iconUrl: "/skills/fastapi.svg",
        url: "https://restfulapi.net/"
      }, {
        name: "GraphQL",
        role: "Data Query Language",
        color: "#e535ab",
        iconUrl: "/skills/graphql.svg",
        url: "https://graphql.org/learn/"
      }]
    }, {
      category: "Agile & Collab",
      icon: Workflow,
      hoverIcon: GitBranch,
      items: [{
        name: "Git",
        role: "Version Control",
        color: "#f05032",
        iconUrl: "/skills/git.svg",
        url: "https://git-scm.com/doc"
      }, {
        name: "Jira",
        role: "Agile Tracking",
        color: "#0052cc",
        iconUrl: "/skills/jira.svg",
        url: "https://support.atlassian.com/jira-software-cloud/"
      }, {
        name: "Trello",
        role: "Kanban Board",
        color: "#0079bf",
        iconUrl: "/skills/trello.svg",
        url: "https://support.atlassian.com/trello/"
      }, {
        name: "Notion",
        role: "Docs & Knowledge",
        color: "#ffffff",
        iconUrl: "/skills/notion.svg",
        url: "https://www.notion.so/help"
      }, {
        name: "Slack",
        role: "Team Comms",
        color: "#e01e5a",
        iconUrl: "/skills/slack.svg",
        url: "https://api.slack.com/"
      }]
    }, {
      category: "AI & Engineering",
      icon: Brain,
      hoverIcon: Bot,
      items: [{
        name: "RAG Pipelines",
        role: "Vector Retrieval & Context",
        color: "#10a37f",
        iconUrl: "/skills/rag.svg",
        url: "https://python.langchain.com/docs/concepts/rag/"
      }, {
        name: "LLM Fine-Tuning",
        role: "Model Optimization & LoRA",
        color: "#8b5cf6",
        iconUrl: "/skills/llm.svg",
        url: "https://huggingface.co/docs/transformers/training"
      }, {
        name: "Prompt Engineering",
        role: "System Prompts & In-Context",
        color: "#f59e0b",
        iconUrl: "/skills/prompting.svg",
        url: "https://www.promptingguide.ai/"
      }]
    }];
    const t20 = isNeumorphic ? "#a3b1c6" : "#d1d5db";
    const t21 = isNeumorphic ? "#1e293b" : "#000000";
    const t22 = isNeumorphic ? "#a3b1c6" : "#d1d5db";
    const t23 = isNeumorphic ? "#1e293b" : "#000000";
    let t24;
    if ($[40] !== t20 || $[41] !== t21 || $[42] !== t22 || $[43] !== t23) {
      t24 = {
        lineColor: t20,
        activeLineColor: t21,
        dotColor: t22,
        activeDotColor: t23,
        dotSize: "1rem",
        titleColor: "inherit",
        activeTitleColor: "inherit",
        descriptionColor: "inherit",
        dateColor: "inherit"
      };
      $[40] = t20;
      $[41] = t21;
      $[42] = t22;
      $[43] = t23;
      $[44] = t24;
    } else {
      t24 = $[44];
    }
    const timelineStyles = t24;
    const t25 = `/${locale}`;
    let t26;
    if ($[45] !== locale) {
      t26 = locale === "ar" ? <ArrowRight size={24} /> : <ArrowLeft size={24} />;
      $[45] = locale;
      $[46] = t26;
    } else {
      t26 = $[46];
    }
    const t27 = dict?.returnGrid || "Return to Grid";
    let t28;
    if ($[47] !== t25 || $[48] !== t26 || $[49] !== t27) {
      t28 = <Link href={t25} className="inline-flex items-center gap-2 text-xl font-bold uppercase mb-6 bg-white text-black hover:bg-black hover:text-white px-3 py-1 border-4 border-transparent hover:border-black transition-all">{t26} {t27}</Link>;
      $[47] = t25;
      $[48] = t26;
      $[49] = t27;
      $[50] = t28;
    } else {
      t28 = $[50];
    }
    const t29 = dict?.about || "About";
    let t30;
    if ($[51] !== t29) {
      t30 = <DecryptText text={t29} />;
      $[51] = t29;
      $[52] = t30;
    } else {
      t30 = $[52];
    }
    const t31 = dict?.me || "me";
    let t32;
    if ($[53] !== t31) {
      t32 = <span className="absolute inset-0 text-white px-4 z-0 pointer-events-none flex items-center" aria-hidden="true"><DecryptText text={t31} /></span>;
      $[53] = t31;
      $[54] = t32;
    } else {
      t32 = $[54];
    }
    const t33 = dict?.me || "me";
    let t34;
    if ($[55] !== t33) {
      t34 = <span className="absolute inset-0 bg-white text-black px-4 z-10 overflow-hidden flex items-center"><DecryptText text={t33} /></span>;
      $[55] = t33;
      $[56] = t34;
    } else {
      t34 = $[56];
    }
    const t35 = dict?.me || "me";
    let t36;
    if ($[57] !== t35) {
      t36 = <span className="relative invisible px-4 z-[-1] pointer-events-none flex items-center" aria-hidden="true"><DecryptText text={t35} /></span>;
      $[57] = t35;
      $[58] = t36;
    } else {
      t36 = $[58];
    }
    let t37;
    if ($[59] !== t32 || $[60] !== t34 || $[61] !== t36) {
      t37 = <span className="relative inline-block align-baseline transform -skew-x-2">{t32}{t34}{t36}</span>;
      $[59] = t32;
      $[60] = t34;
      $[61] = t36;
      $[62] = t37;
    } else {
      t37 = $[62];
    }
    let t38;
    if ($[63] !== t30 || $[64] !== t37) {
      t38 = <h1 className="text-6xl md:text-8xl font-black uppercase tracking-tighter leading-none flex flex-wrap items-baseline gap-4 text-white">{t30}{t37}</h1>;
      $[63] = t30;
      $[64] = t37;
      $[65] = t38;
    } else {
      t38 = $[65];
    }
    let t39;
    if ($[66] !== t28 || $[67] !== t38) {
      t39 = <div>{t28}{t38}</div>;
      $[66] = t28;
      $[67] = t38;
      $[68] = t39;
    } else {
      t39 = $[68];
    }
    let t40;
    if ($[69] !== dict) {
      t40 = <div className="w-full md:w-auto shrink-0 relative z-10"><AudioPlayer dict={dict} /></div>;
      $[69] = dict;
      $[70] = t40;
    } else {
      t40 = $[70];
    }
    let t41;
    if ($[71] !== t39 || $[72] !== t40) {
      t41 = <div className="animate-slide-up max-w-7xl mx-auto mb-8 md:mb-10 flex flex-col md:flex-row md:items-end justify-between gap-12 md:gap-6">{t39}{t40}</div>;
      $[71] = t39;
      $[72] = t40;
      $[73] = t41;
    } else {
      t41 = $[73];
    }
    const t42 = `brutalist-container flex flex-col justify-between static-card-container ${isNeumorphic ? "" : "!bg-black !text-white brutalist-container-dark"}`;
    const t43 = `flex items-center gap-4 border-b-4 pb-4 mb-6 transition-all duration-300 ${isNeumorphic ? "border-[#a3b1c6]" : "border-white"}`;
    let t44;
    if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
      t44 = <EducationIcon size={40} />;
      $[74] = t44;
    } else {
      t44 = $[74];
    }
    const t45 = dict?.education || "Education";
    let t46;
    if ($[75] !== t45) {
      t46 = <h2 className="text-4xl font-black uppercase">{t45}</h2>;
      $[75] = t45;
      $[76] = t46;
    } else {
      t46 = $[76];
    }
    let t47;
    if ($[77] !== t43 || $[78] !== t46) {
      t47 = <div className={t43}>{t44}{t46}</div>;
      $[77] = t43;
      $[78] = t46;
      $[79] = t47;
    } else {
      t47 = $[79];
    }
    const t48 = dict?.eduSchool || "Ain Shams University";
    let t49;
    if ($[80] !== t48) {
      t49 = <Image src="/about/ainshams.png" alt={t48} width={36} height={36} className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0" priority={true} />;
      $[80] = t48;
      $[81] = t49;
    } else {
      t49 = $[81];
    }
    const t50 = dict?.eduSchool || "Ain Shams University";
    let t51;
    if ($[82] !== t50) {
      t51 = <h3 className="text-3xl font-bold uppercase leading-tight">{t50}</h3>;
      $[82] = t50;
      $[83] = t51;
    } else {
      t51 = $[83];
    }
    let t52;
    if ($[84] !== t49 || $[85] !== t51) {
      t52 = <div className="flex items-center gap-3 mb-2">{t49}{t51}</div>;
      $[84] = t49;
      $[85] = t51;
      $[86] = t52;
    } else {
      t52 = $[86];
    }
    const t53 = `text-xl font-bold mb-6 uppercase transition-all duration-300 ${isNeumorphic ? "text-zinc-500" : "text-zinc-400"}`;
    const t54 = dict?.eduDegree || "Bachelor of Commerce (B.B.A.) // 2019 - 2023";
    let t55;
    if ($[87] !== t53 || $[88] !== t54) {
      t55 = <p className={t53}>{t54}</p>;
      $[87] = t53;
      $[88] = t54;
      $[89] = t55;
    } else {
      t55 = $[89];
    }
    const t56 = dict?.eduDesc || "Specialized in accounting and project management, which strengthened my problem-solving skills and gave me a solid understanding of real-world product needs.";
    let t57;
    if ($[90] !== t56) {
      t57 = <p className="text-lg md:text-xl font-medium leading-relaxed">{t56}</p>;
      $[90] = t56;
      $[91] = t57;
    } else {
      t57 = $[91];
    }
    let t58;
    if ($[92] !== t47 || $[93] !== t52 || $[94] !== t55 || $[95] !== t57) {
      t58 = <div>{t47}{t52}{t55}{t57}</div>;
      $[92] = t47;
      $[93] = t52;
      $[94] = t55;
      $[95] = t57;
      $[96] = t58;
    } else {
      t58 = $[96];
    }
    let t59;
    if ($[97] !== t42 || $[98] !== t58) {
      t59 = <section className={t42}>{t58}</section>;
      $[97] = t42;
      $[98] = t58;
      $[99] = t59;
    } else {
      t59 = $[99];
    }
    const t60 = `brutalist-container flex flex-col text-black overflow-hidden ${isNeumorphic ? "" : "brutalist-shadow-dark"}`;
    const t61 = `flex items-center gap-4 border-b-4 pb-3 mb-4 transition-all duration-300 ${isNeumorphic ? "border-[#a3b1c6]" : "border-black"}`;
    let t62;
    if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
      t62 = <ClearancesIcon size={40} />;
      $[100] = t62;
    } else {
      t62 = $[100];
    }
    const t63 = dict?.clearances || "Clearances";
    let t64;
    if ($[101] !== t63) {
      t64 = <h2 className="text-4xl font-black uppercase">{t63}</h2>;
      $[101] = t63;
      $[102] = t64;
    } else {
      t64 = $[102];
    }
    let t65;
    if ($[103] !== t61 || $[104] !== t64) {
      t65 = <div className={t61}>{t62}{t64}</div>;
      $[103] = t61;
      $[104] = t64;
      $[105] = t65;
    } else {
      t65 = $[105];
    }
    let t66;
    if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
      t66 = [{
        id: "1",
        title: ""
      }, {
        id: "2",
        title: ""
      }, {
        id: "3",
        title: ""
      }];
      $[106] = t66;
    } else {
      t66 = $[106];
    }
    let t67;
    if ($[107] !== dict?.cs50 || $[108] !== dict?.dataAnalysis || $[109] !== dict?.mernStack || $[110] !== dict?.newTab || $[111] !== dict?.notOnline || $[112] !== isNeumorphic) {
      t67 = event => {
        if (event.id === "1") {
          return <a onClick={e => {
            e.preventDefault();
            showToast(dict?.notOnline || "MERN Stack certificate is not online yet.");
          }} className={`group block border-s-8 ps-4 pe-3 py-1.5 transition-all cursor-pointer w-full text-left rtl:text-right ${isNeumorphic ? "border-[#a3b1c6] hover:bg-[#d1d9e6] hover:text-[#1e293b] active:bg-[#d1d9e6] active:text-[#1e293b]" : "border-black hover:bg-black hover:text-white active:bg-black active:text-white"}`}><div className="flex justify-between items-center w-full min-w-0"><div className="min-w-0 flex-1"><h3 className="text-2xl font-black uppercase leading-tight">{dict?.mernStack || "MERN Stack & Gen AI"}</h3><div className="flex items-center gap-3 mt-1"><p className="text-lg font-bold text-zinc-500 group-hover:text-zinc-300 group-active:text-zinc-300 uppercase leading-none whitespace-nowrap">ITI (MCIT)</p><span className="text-lg font-bold text-zinc-600 group-hover:text-zinc-400 group-active:text-zinc-400 transition-colors leading-none whitespace-nowrap">{"// 2026"}</span></div></div></div></a>;
        }
        if (event.id === "2") {
          return <a href="https://cs50.harvard.edu/certificates/09d4b4ad-f9dd-4cf3-a1dc-7385742119f9" target="_blank" rel="noopener noreferrer" className={`group block border-s-8 ps-4 pe-3 py-1.5 transition-all cursor-pointer w-full text-left rtl:text-right ${isNeumorphic ? "border-[#a3b1c6] hover:bg-[#d1d9e6] hover:text-[#1e293b] active:bg-[#d1d9e6] active:text-[#1e293b]" : "border-black hover:bg-black hover:text-white active:bg-black active:text-white"}`}><div className="flex justify-between items-center w-full min-w-0"><div className="min-w-0 flex-1"><h3 className="text-2xl font-black uppercase leading-tight">{dict?.cs50 || "CS50x"}</h3><div className="flex items-center gap-3 mt-1"><p className="text-lg font-bold text-zinc-500 group-hover:text-zinc-300 group-active:text-zinc-300 uppercase leading-none whitespace-nowrap">edX (Harvard)</p><span className="text-lg font-bold text-zinc-600 group-hover:text-zinc-400 group-active:text-zinc-400 transition-colors leading-none whitespace-nowrap">{"// 2025"}</span></div></div><ExternalLink size={24} className="opacity-0 group-hover:opacity-100 group-active:opacity-100 rtl:translate-x-4 ltr:-translate-x-4 group-hover:translate-x-0 group-active:translate-x-0 transition-all duration-300 ms-3 me-1 shrink-0" /><span className="sr-only">{dict?.newTab || " (opens in a new tab)"}</span></div></a>;
        }
        if (event.id === "3") {
          return <a href="https://i.ibb.co/ynPJ6szk/FWD-data-Certificate.png" target="_blank" rel="noopener noreferrer" className={`group block border-s-8 ps-4 pe-3 py-1.5 transition-all cursor-pointer w-full text-left rtl:text-right ${isNeumorphic ? "border-[#a3b1c6] hover:bg-[#d1d9e6] hover:text-[#1e293b] active:bg-[#d1d9e6] active:text-[#1e293b]" : "border-black hover:bg-black hover:text-white active:bg-black active:text-white"}`}><div className="flex justify-between items-center w-full min-w-0"><div className="min-w-0 flex-1"><h3 className="text-2xl font-black uppercase leading-tight">{dict?.dataAnalysis || "Data Analysis"}</h3><div className="flex items-center gap-3 mt-1"><p className="text-lg font-bold text-zinc-500 group-hover:text-zinc-300 group-active:text-zinc-300 uppercase leading-none whitespace-nowrap">Egypt FWD (MCIT)</p><span className="text-lg font-bold text-zinc-600 group-hover:text-zinc-400 group-active:text-zinc-400 transition-colors leading-none whitespace-nowrap">{"// 2021"}</span></div></div><ExternalLink size={24} className="opacity-0 group-hover:opacity-100 group-active:opacity-100 rtl:translate-x-4 ltr:-translate-x-4 group-hover:translate-x-0 group-active:translate-x-0 transition-all duration-300 ms-3 me-1 shrink-0" /><span className="sr-only">{dict?.newTab || " (opens in a new tab)"}</span></div></a>;
        }
        return null;
      };
      $[107] = dict?.cs50;
      $[108] = dict?.dataAnalysis;
      $[109] = dict?.mernStack;
      $[110] = dict?.newTab;
      $[111] = dict?.notOnline;
      $[112] = isNeumorphic;
      $[113] = t67;
    } else {
      t67 = $[113];
    }
    let t68;
    if ($[114] !== t67 || $[115] !== timelineStyles) {
      t68 = <AnimatedTimeline events={t66} styles={timelineStyles} className="py-0 my-0" reverseDirection={true} customEventRender={t67} />;
      $[114] = t67;
      $[115] = timelineStyles;
      $[116] = t68;
    } else {
      t68 = $[116];
    }
    let t69;
    if ($[117] !== t60 || $[118] !== t65 || $[119] !== t68) {
      t69 = <section className={t60}>{t65}{t68}</section>;
      $[117] = t60;
      $[118] = t65;
      $[119] = t68;
      $[120] = t69;
    } else {
      t69 = $[120];
    }
    let t70;
    if ($[121] !== t59 || $[122] !== t69) {
      t70 = <div className="max-w-7xl mx-auto flex flex-col gap-8 md:gap-10 relative z-10"><div className="animate-slide-up-delay-1 grid grid-cols-1 lg:grid-cols-2 gap-12">{t59}{t69}</div></div>;
      $[121] = t59;
      $[122] = t69;
      $[123] = t70;
    } else {
      t70 = $[123];
    }
    let t71;
    if ($[124] !== t41 || $[125] !== t70) {
      t71 = <>{t41}{t70}</>;
      $[124] = t41;
      $[125] = t70;
      $[126] = t71;
    } else {
      t71 = $[126];
    }
    const topContent = t71;
    const t72 = `relative w-full ${isNeumorphic ? "bg-[#e0e5ec]" : "bg-white"}`;
    let t73;
    if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
      t73 = <svg className="absolute w-0 h-0 overflow-hidden" aria-hidden="true"><defs><filter id="about-glass-filter"><feTurbulence type="fractalNoise" baseFrequency="0.12 0.12" numOctaves="1" result="warp" /><feDisplacementMap xChannelSelector="R" yChannelSelector="G" scale="15" in="SourceGraphic" in2="warp" /></filter></defs></svg>;
      $[127] = t73;
    } else {
      t73 = $[127];
    }
    let t74;
    if ($[128] !== clipPathImage) {
      t74 = {
        clipPath: clipPathImage
      };
      $[128] = clipPathImage;
      $[129] = t74;
    } else {
      t74 = $[129];
    }
    let t75;
    if ($[130] !== imageY) {
      t75 = {
        y: imageY
      };
      $[130] = imageY;
      $[131] = t75;
    } else {
      t75 = $[131];
    }
    const t76 = `/about-${imageIndex}.jpg`;
    let t77;
    if ($[132] !== t75 || $[133] !== t76) {
      t77 = <motion.img style={t75} src={t76} alt="" className="absolute -top-[5%] h-[110%] left-0 w-full object-cover object-center" />;
      $[132] = t75;
      $[133] = t76;
      $[134] = t77;
    } else {
      t77 = $[134];
    }
    let t78;
    if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
      t78 = <div className="absolute -top-[5%] h-[110%] left-0 bg-black/30 w-full" />;
      $[135] = t78;
    } else {
      t78 = $[135];
    }
    let t79;
    if ($[136] !== t74 || $[137] !== t77) {
      t79 = <motion.div style={t74} className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none">{t77}{t78}</motion.div>;
      $[136] = t74;
      $[137] = t77;
      $[138] = t79;
    } else {
      t79 = $[138];
    }
    let t80;
    if ($[139] !== topContent) {
      t80 = <div className="relative z-10 px-6 md:px-12 lg:px-24 pt-36 md:pt-30 pb-6">{topContent}</div>;
      $[139] = topContent;
      $[140] = t80;
    } else {
      t80 = $[140];
    }
    let t81;
    if ($[141] !== clipPathFilter) {
      t81 = <motion.div style={{
        clipPath: clipPathFilter,
        backdropFilter: "invert(1) hue-rotate(180deg)"
      }} className="absolute top-0 left-0 w-full h-full z-20 pointer-events-none about-glass-overlay" aria-hidden="true" />;
      $[141] = clipPathFilter;
      $[142] = t81;
    } else {
      t81 = $[142];
    }
    let t82;
    if ($[143] !== t72 || $[144] !== t79 || $[145] !== t80 || $[146] !== t81) {
      t82 = <div className={t72}>{t73}{t79}{t80}{t81}</div>;
      $[143] = t72;
      $[144] = t79;
      $[145] = t80;
      $[146] = t81;
      $[147] = t82;
    } else {
      t82 = $[147];
    }
    section1 = t82;
    const t83 = isNeumorphic ? "rgba(0, 0, 0, 0.18)" : "rgba(255, 255, 255, 0.22)";
    let t84;
    if ($[148] !== t83) {
      t84 = <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" preserveAspectRatio="none" viewBox="0 0 1440 800" fill="none" aria-hidden="true"><path d="M -100,400 Q 360,200 720,400 T 1540,400" stroke={t83} strokeWidth="3" strokeLinecap="round" /></svg>;
      $[148] = t83;
      $[149] = t84;
    } else {
      t84 = $[149];
    }
    const t85 = dict?.whoIAm || "Who I Am";
    const t86 = dict?.summary || "Summary";
    let t87;
    if ($[150] !== t85 || $[151] !== t86) {
      t87 = [t85, t86];
      $[150] = t85;
      $[151] = t86;
      $[152] = t87;
    } else {
      t87 = $[152];
    }
    let t88;
    if ($[153] !== isSummaryCardHovered || $[154] !== t87) {
      t88 = <div className="inline-block bg-black text-white px-6 py-2 mb-8 transform -skew-x-2"><MorphingText texts={t87} className="text-4xl font-black uppercase tracking-widest text-white m-0 p-0" forceHover={isSummaryCardHovered} /></div>;
      $[153] = isSummaryCardHovered;
      $[154] = t87;
      $[155] = t88;
    } else {
      t88 = $[155];
    }
    let t89;
    let t90;
    if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
      t89 = () => setIsSummaryCardHovered(true);
      t90 = () => setIsSummaryCardHovered(false);
      $[156] = t89;
      $[157] = t90;
    } else {
      t89 = $[156];
      t90 = $[157];
    }
    const t91 = `p-8 md:p-10 transition-all duration-300 hover:!transform-none ${isNeumorphic ? "brutalist-container bg-white text-black static-card-container" : "brutalist-container-dark !shadow-[8px_8px_0px_0px_rgba(255,255,255,0.3)]"}`;
    const text = dict?.bioP1 || "My journey began with Python automation and scripting, building tools to scrape data and automate tasks. I then expanded into full-stack development, mastering the MERN stack to engineer dynamic applications.";
    let t92;
    if ($[158] !== text) {
      t92 = text.charAt(0);
      $[158] = text;
      $[159] = t92;
    } else {
      t92 = $[159];
    }
    const first = t92;
    let t93;
    if ($[160] !== text) {
      t93 = text.slice(1);
      $[160] = text;
      $[161] = t93;
    } else {
      t93 = $[161];
    }
    const rest = t93;
    const t94 = isNeumorphic ? "#111" : "#fff";
    let t95;
    if ($[162] !== t94) {
      t95 = {
        color: t94
      };
      $[162] = t94;
      $[163] = t95;
    } else {
      t95 = $[163];
    }
    let t96;
    if ($[164] !== first || $[165] !== t95) {
      t96 = <span className="drop-cap-first select-none" aria-hidden="true" style={t95}>{first}</span>;
      $[164] = first;
      $[165] = t95;
      $[166] = t96;
    } else {
      t96 = $[166];
    }
    let t97;
    if ($[167] !== first) {
      t97 = <span className="sr-only">{first}</span>;
      $[167] = first;
      $[168] = t97;
    } else {
      t97 = $[168];
    }
    let t98;
    if ($[169] !== rest || $[170] !== t96 || $[171] !== t97) {
      t98 = <p><>{t96}{t97}{rest}</></p>;
      $[169] = rest;
      $[170] = t96;
      $[171] = t97;
      $[172] = t98;
    } else {
      t98 = $[172];
    }
    let t99;
    if ($[173] === Symbol.for("react.memo_cache_sentinel")) {
      t99 = {
        clear: "both"
      };
      $[173] = t99;
    } else {
      t99 = $[173];
    }
    const t100 = dict?.bioP2 || "Today, I focus on building complete, containerized applications using Docker, ensuring that what runs on my machine runs everywhere.";
    let t101;
    if ($[174] !== t100) {
      t101 = <p style={t99}>{t100}</p>;
      $[174] = t100;
      $[175] = t101;
    } else {
      t101 = $[175];
    }
    const t102 = dict?.bioP3 || "When I'm not coding, you can find me exploring retro tech, playing classic games, or experimenting with 3D web graphics.";
    let t103;
    if ($[176] !== t102) {
      t103 = <p>{t102}</p>;
      $[176] = t102;
      $[177] = t103;
    } else {
      t103 = $[177];
    }
    let t104;
    if ($[178] !== t101 || $[179] !== t103 || $[180] !== t98) {
      t104 = <div className="flex flex-col gap-6 text-lg md:text-xl font-medium leading-relaxed">{t98}{t101}{t103}</div>;
      $[178] = t101;
      $[179] = t103;
      $[180] = t98;
      $[181] = t104;
    } else {
      t104 = $[181];
    }
    let t105;
    if ($[182] !== t104 || $[183] !== t91) {
      t105 = <div onMouseEnter={t89} onMouseLeave={t90} className={t91}>{t104}</div>;
      $[182] = t104;
      $[183] = t91;
      $[184] = t105;
    } else {
      t105 = $[184];
    }
    let t106;
    if ($[185] !== t105 || $[186] !== t88) {
      t106 = <div className="w-full relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24"><section className="animate-slide-up-delay-2">{t88}{t105}</section></div>;
      $[185] = t105;
      $[186] = t88;
      $[187] = t106;
    } else {
      t106 = $[187];
    }
    let t107;
    if ($[188] !== t106 || $[189] !== t84) {
      t107 = <div className="relative w-full">{t84}{t106}</div>;
      $[188] = t106;
      $[189] = t84;
      $[190] = t107;
    } else {
      t107 = $[190];
    }
    section2 = t107;
    t18 = "relative w-full py-6 md:py-8";
    const t108 = isNeumorphic ? "rgba(0, 0, 0, 0.16)" : "rgba(255, 255, 255, 0.20)";
    let t109;
    if ($[191] !== t108) {
      t109 = <path d="M -100,300 C 300,100 600,600 1000,200 T 1540,500" stroke={t108} strokeWidth="3" strokeLinecap="round" />;
      $[191] = t108;
      $[192] = t109;
    } else {
      t109 = $[192];
    }
    const t110 = isNeumorphic ? "rgba(0, 0, 0, 0.10)" : "rgba(255, 255, 255, 0.12)";
    let t111;
    if ($[193] !== t110) {
      t111 = <path d="M -100,500 C 400,700 700,200 1100,600 T 1540,300" stroke={t110} strokeWidth="2" strokeDasharray="8,8" strokeLinecap="round" />;
      $[193] = t110;
      $[194] = t111;
    } else {
      t111 = $[194];
    }
    if ($[195] !== t109 || $[196] !== t111) {
      t19 = <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" preserveAspectRatio="none" viewBox="0 0 1440 800" fill="none" aria-hidden="true">{t109}{t111}</svg>;
      $[195] = t109;
      $[196] = t111;
      $[197] = t19;
    } else {
      t19 = $[197];
    }
    t17 = "w-full relative z-10 max-w-[85rem] mx-auto px-4 sm:px-6 md:px-8 py-4";
    t15 = "animate-slide-up-delay-2";
    const t112 = dict?.techArsenal || "Technical Arsenal";
    const t113 = dict?.skills || "Skills";
    let t114;
    if ($[198] !== t112 || $[199] !== t113) {
      t114 = [t112, t113];
      $[198] = t112;
      $[199] = t113;
      $[200] = t114;
    } else {
      t114 = $[200];
    }
    if ($[201] !== isSkillsCardHovered || $[202] !== t114) {
      t16 = <div className="inline-block bg-black text-white px-5 py-1.5 mb-6 transform -skew-x-2"><MorphingText texts={t114} className="text-3xl md:text-4xl font-black uppercase tracking-widest text-white m-0 p-0" forceHover={isSkillsCardHovered} /></div>;
      $[201] = isSkillsCardHovered;
      $[202] = t114;
      $[203] = t16;
    } else {
      t16 = $[203];
    }
    t12 = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5";
    t13 = "ltr";
    t14 = stack.map((category, index) => <div key={index} onMouseEnter={() => {
      setIsSkillsCardHovered(true);
      setHoveredCardIndex(index);
    }} onMouseLeave={() => {
      setIsSkillsCardHovered(false);
      setHoveredCardIndex(null);
    }} className={`group transition-all duration-300 flex flex-col justify-between p-3.5 sm:p-4 ${isNeumorphic ? "brutalist-container hover:!bg-[#d1d9e6] hover:!text-[#1e293b] hover:!translate-x-1 hover:!translate-y-1 hover:!shadow-none" : "brutalist-container-dark hover:!translate-x-1 hover:!translate-y-1 hover:!shadow-none"}`}><div><div className={`flex items-center gap-3 border-b-2 pb-2.5 mb-3 transition-all duration-300 ${isNeumorphic ? "border-[#a3b1c6]" : "border-white/30"}`}><CategoryIconMorph icon={category.icon} hoverIcon={category.hoverIcon} isHovered={hoveredCardIndex === index} isNeumorphic={isNeumorphic} /><h3 className="text-sm sm:text-base font-black uppercase tracking-tight leading-tight">{category.category}</h3></div><div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{category.items.map((item, i) => {
            const isAllFullWidth = category.category === "AI & Engineering";
            const isLastOddItem = category.items.length % 2 !== 0 && i === category.items.length - 1;
            return <div key={i} className={isAllFullWidth || isLastOddItem ? "sm:col-span-2" : ""}><SkillKeycap item={item} isNeumorphic={isNeumorphic} /></div>;
          })}</div></div></div>);
    $[20] = clipPathFilter;
    $[21] = clipPathImage;
    $[22] = dict;
    $[23] = hoveredCardIndex;
    $[24] = imageIndex;
    $[25] = imageY;
    $[26] = isNeumorphic;
    $[27] = isSkillsCardHovered;
    $[28] = isSummaryCardHovered;
    $[29] = locale;
    $[30] = section1;
    $[31] = section2;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    section1 = $[30];
    section2 = $[31];
    t12 = $[32];
    t13 = $[33];
    t14 = $[34];
    t15 = $[35];
    t16 = $[36];
    t17 = $[37];
    t18 = $[38];
    t19 = $[39];
  }
  let t20;
  if ($[204] !== t12 || $[205] !== t13 || $[206] !== t14) {
    t20 = <div className={t12} dir={t13}>{t14}</div>;
    $[204] = t12;
    $[205] = t13;
    $[206] = t14;
    $[207] = t20;
  } else {
    t20 = $[207];
  }
  let t21;
  if ($[208] !== t15 || $[209] !== t16 || $[210] !== t20) {
    t21 = <section className={t15}>{t16}{t20}</section>;
    $[208] = t15;
    $[209] = t16;
    $[210] = t20;
    $[211] = t21;
  } else {
    t21 = $[211];
  }
  let t22;
  if ($[212] !== t17 || $[213] !== t21) {
    t22 = <div className={t17}>{t21}</div>;
    $[212] = t17;
    $[213] = t21;
    $[214] = t22;
  } else {
    t22 = $[214];
  }
  let t23;
  if ($[215] !== t18 || $[216] !== t19 || $[217] !== t22) {
    t23 = <div className={t18}>{t19}{t22}</div>;
    $[215] = t18;
    $[216] = t19;
    $[217] = t22;
    $[218] = t23;
  } else {
    t23 = $[218];
  }
  const section3 = t23;
  const t24 = isNeumorphic ? "rgba(0, 0, 0, 0.20)" : "rgba(255, 255, 255, 0.24)";
  let t25;
  if ($[219] !== t24) {
    t25 = <path d="M -100,200 C 300,600 500,-100 900,500 T 1540,100" stroke={t24} strokeWidth="4" strokeLinecap="round" />;
    $[219] = t24;
    $[220] = t25;
  } else {
    t25 = $[220];
  }
  const t26 = isNeumorphic ? "rgba(0, 0, 0, 0.14)" : "rgba(255, 255, 255, 0.16)";
  let t27;
  if ($[221] !== t26) {
    t27 = <path d="M -100,600 C 200,100 700,800 1000,300 T 1540,700" stroke={t26} strokeWidth="3" strokeLinecap="round" />;
    $[221] = t26;
    $[222] = t27;
  } else {
    t27 = $[222];
  }
  const t28 = isNeumorphic ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.10)";
  let t29;
  if ($[223] !== t28) {
    t29 = <path d="M -100,400 C 400,300 600,600 1100,200 T 1540,400" stroke={t28} strokeWidth="2" strokeDasharray="6,6" strokeLinecap="round" />;
    $[223] = t28;
    $[224] = t29;
  } else {
    t29 = $[224];
  }
  const t30 = isNeumorphic ? "rgba(0, 0, 0, 0.06)" : "rgba(255, 255, 255, 0.08)";
  let t31;
  if ($[225] !== t30) {
    t31 = <path d="M -100,450 Q 100,350 300,450 T 700,450 T 1100,450 T 1540,450" stroke={t30} strokeWidth="1.5" strokeLinecap="round" />;
    $[225] = t30;
    $[226] = t31;
  } else {
    t31 = $[226];
  }
  let t32;
  if ($[227] !== t25 || $[228] !== t27 || $[229] !== t29 || $[230] !== t31) {
    t32 = <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" preserveAspectRatio="none" viewBox="0 0 1440 800" fill="none" aria-hidden="true">{t25}{t27}{t29}{t31}</svg>;
    $[227] = t25;
    $[228] = t27;
    $[229] = t29;
    $[230] = t31;
    $[231] = t32;
  } else {
    t32 = $[231];
  }
  const t33 = `p-8 md:p-10 transition-all duration-300 ${isNeumorphic ? "brutalist-container hover:!translate-x-1 hover:!translate-y-1 hover:!shadow-none" : "brutalist-container-dark hover:!translate-x-1 hover:!translate-y-1 hover:!shadow-none"}`;
  const t34 = dict?.legacySys || "Legacy Systems";
  let t35;
  if ($[232] !== t34) {
    t35 = <h2 className="text-3xl md:text-4xl font-black uppercase leading-tight">{t34}</h2>;
    $[232] = t34;
    $[233] = t35;
  } else {
    t35 = $[233];
  }
  const t36 = `text-lg md:text-xl font-bold uppercase mt-1 ${isNeumorphic ? "text-zinc-500" : "text-zinc-400"}`;
  const t37 = dict?.legacyDesc || "Explore previous portfolio iterations";
  let t38;
  if ($[234] !== t36 || $[235] !== t37) {
    t38 = <p className={t36}>{t37}</p>;
    $[234] = t36;
    $[235] = t37;
    $[236] = t38;
  } else {
    t38 = $[236];
  }
  let t39;
  if ($[237] !== t35 || $[238] !== t38) {
    t39 = <div className="flex flex-col text-center md:text-left rtl:md:text-right">{t35}{t38}</div>;
    $[237] = t35;
    $[238] = t38;
    $[239] = t39;
  } else {
    t39 = $[239];
  }
  const t40 = `px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-center gap-2 sm:gap-3 text-base sm:text-xl font-black uppercase whitespace-nowrap transition-all duration-300 group ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563] rounded-xl shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] hover:shadow-[8px_8px_16px_rgba(163,177,198,0.7),_-8px_-8px_16px_rgba(255,255,255,0.6)] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]" : "bg-transparent text-white border-4 border-white shadow-[8px_8px_0px_rgba(255,255,255,0.3)] hover:shadow-[4px_4px_0px_rgba(255,255,255,0.3)] hover:translate-x-1 hover:translate-y-1 active:shadow-none active:translate-x-2 active:translate-y-2"}`;
  const t41 = dict?.v1 || "Version 1.0";
  let t42;
  if ($[240] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Compass className="w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:rotate-[360deg] shrink-0" />;
    $[240] = t42;
  } else {
    t42 = $[240];
  }
  const t43 = dict?.newTab || " (opens in a new tab)";
  let t44;
  if ($[241] !== t43) {
    t44 = <span className="sr-only">{t43}</span>;
    $[241] = t43;
    $[242] = t44;
  } else {
    t44 = $[242];
  }
  let t45;
  if ($[243] !== t40 || $[244] !== t41 || $[245] !== t44) {
    t45 = <RippleButton href="https://my-portfolio-seven-beta-98.vercel.app/" target="_blank" rel="noopener noreferrer" className={t40}>{t41}{t42}{t44}</RippleButton>;
    $[243] = t40;
    $[244] = t41;
    $[245] = t44;
    $[246] = t45;
  } else {
    t45 = $[246];
  }
  const t46 = `px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-center gap-2 sm:gap-3 text-base sm:text-xl font-black uppercase whitespace-nowrap transition-all duration-300 group ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563] rounded-xl shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] hover:shadow-[8px_8px_16px_rgba(163,177,198,0.7),_-8px_-8px_16px_rgba(255,255,255,0.6)] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]" : "bg-transparent text-white border-4 border-white shadow-[8px_8px_0px_rgba(255,255,255,0.3)] hover:shadow-[4px_4px_0px_rgba(255,255,255,0.3)] hover:translate-x-1 hover:translate-y-1 active:shadow-none active:translate-x-2 active:translate-y-2"}`;
  const t47 = dict?.v2 || "Version 2.0";
  let t48;
  if ($[247] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Compass className="w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:rotate-[360deg] shrink-0" />;
    $[247] = t48;
  } else {
    t48 = $[247];
  }
  const t49 = dict?.newTab || " (opens in a new tab)";
  let t50;
  if ($[248] !== t49) {
    t50 = <span className="sr-only">{t49}</span>;
    $[248] = t49;
    $[249] = t50;
  } else {
    t50 = $[249];
  }
  let t51;
  if ($[250] !== t46 || $[251] !== t47 || $[252] !== t50) {
    t51 = <RippleButton href="https://samybit.github.io/brutalist-portfolio/" target="_blank" rel="noopener noreferrer" className={t46}>{t47}{t48}{t50}</RippleButton>;
    $[250] = t46;
    $[251] = t47;
    $[252] = t50;
    $[253] = t51;
  } else {
    t51 = $[253];
  }
  let t52;
  if ($[254] !== t45 || $[255] !== t51) {
    t52 = <div className="flex flex-col sm:flex-row w-full md:w-auto gap-6 shrink-0">{t45}{t51}</div>;
    $[254] = t45;
    $[255] = t51;
    $[256] = t52;
  } else {
    t52 = $[256];
  }
  let t53;
  if ($[257] !== t39 || $[258] !== t52) {
    t53 = <div className="flex flex-col md:flex-row items-center justify-between gap-8">{t39}{t52}</div>;
    $[257] = t39;
    $[258] = t52;
    $[259] = t53;
  } else {
    t53 = $[259];
  }
  let t54;
  if ($[260] !== t33 || $[261] !== t53) {
    t54 = <div className="w-full relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex-grow flex flex-col justify-center py-12"><div className="animate-slide-up-delay-2 w-full"><section className={t33}>{t53}</section></div></div>;
    $[260] = t33;
    $[261] = t53;
    $[262] = t54;
  } else {
    t54 = $[262];
  }
  let t55;
  if ($[263] !== t32 || $[264] !== t54) {
    t55 = <div className="relative w-full flex flex-col justify-between transition-colors duration-300">{t32}{t54}</div>;
    $[263] = t32;
    $[264] = t54;
    $[265] = t55;
  } else {
    t55 = $[265];
  }
  const section4 = t55;
  let t56;
  if ($[266] !== isNeumorphic || $[267] !== locale || $[268] !== toastMessage) {
    t56 = toastMessage ? <div id="toast-notification" className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 p-4 border-4 border-black bg-white text-black font-black uppercase text-sm flex items-center justify-between animate-slide-up min-w-[280px] sm:min-w-[350px] overflow-hidden ${isNeumorphic ? "shadow-[8px_8px_0px_0px_#000000]" : "shadow-[8px_8px_0px_0px_rgba(255,255,255,0.3)]"}`}><span className="relative z-10">{toastMessage}</span><button onClick={() => setToastMessage(null)} className="font-black text-xl hover:text-zinc-600 transition-colors ms-6 cursor-pointer relative z-10">×</button><motion.div initial={{
        width: "100%"
      }} animate={{
        width: "0%"
      }} transition={{
        duration: 4,
        ease: "linear"
      }} className={`absolute bottom-0 ${locale === "ar" ? "right-0" : "left-0"} h-[6px] bg-black`} /></div> : null;
    $[266] = isNeumorphic;
    $[267] = locale;
    $[268] = toastMessage;
    $[269] = t56;
  } else {
    t56 = $[269];
  }
  const toastOverlay = t56;
  const t57 = `min-h-screen flex flex-col overflow-x-hidden transition-colors duration-300 ${isNeumorphic ? "bg-[#e0e5ec]" : "bg-black text-white"}`;
  const t58 = locale === "ar" ? "rtl" : "ltr";
  let t59;
  if ($[270] !== section2 || $[271] !== section3 || $[272] !== section4) {
    t59 = <div className="w-full flex flex-col gap-16 mt-8 mb-24 relative z-10">{section2}{section3}{section4}</div>;
    $[270] = section2;
    $[271] = section3;
    $[272] = section4;
    $[273] = t59;
  } else {
    t59 = $[273];
  }
  let t60;
  if ($[274] !== section1 || $[275] !== t59) {
    t60 = <div className="flex-grow flex flex-col">{section1}{t59}</div>;
    $[274] = section1;
    $[275] = t59;
    $[276] = t60;
  } else {
    t60 = $[276];
  }
  let t61;
  if ($[277] !== footerDict) {
    t61 = <CarouselSection footer={<Footer dict={footerDict} overlay={true} />} />;
    $[277] = footerDict;
    $[278] = t61;
  } else {
    t61 = $[278];
  }
  let t62;
  if ($[279] !== t57 || $[280] !== t58 || $[281] !== t60 || $[282] !== t61 || $[283] !== toastOverlay) {
    t62 = <main className={t57} dir={t58}>{t60}{t61}{toastOverlay}</main>;
    $[279] = t57;
    $[280] = t58;
    $[281] = t60;
    $[282] = t61;
    $[283] = toastOverlay;
    $[284] = t62;
  } else {
    t62 = $[284];
  }
  return t62;
}
function _temp5() {
  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }
}
function _temp4(val_0) {
  return `inset(${val_0}% 0% 0% 0%)`;
}
function _temp3(val) {
  return `inset(0% 0% ${val}% 0%)`;
}
function _temp2(y, start, end) {
  const range = end - start;
  if (range <= 0) {
    return 0;
  }
  return Math.min(Math.max((y - start) / range, 0), 1);
}
function _temp() {
  return getAboutImageIndex();
}